import moment from 'moment';
import win from '../../shared/win';

export const icon = {
  team_illustration: 'https://storage.googleapis.com/fplsblog/teams-icons-01/team-illustration.svg',
  empty_inbox: 'https://storage.googleapis.com/fplsblog/teams-icons-01/empty-inbox-outline.svg',
  switch_filled: 'https://storage.googleapis.com/fplsblog/teams-icons-01/switch-filled.svg',
  message_sent: 'https://storage.googleapis.com/fplsblog/teams-icons-01/message_sent.svg',
  minus_circle: 'https://storage.googleapis.com/fplsblog/teams-icons-01/minus-circle.svg',
  switch_white: 'https://storage.googleapis.com/fplsblog/teams-icons-01/switch-white.svg',
  banner_dots: 'https://storage.googleapis.com/fplsblog/teams-icons-01/banner-dots.svg',
  add_circle: 'https://storage.googleapis.com/fplsblog/teams-icons-01/add-circle.svg',
  view: 'https://storage.googleapis.com/fplsblog/teams-icons-01/fluent-open.svg',
  delete_confirm: 'https://storage.googleapis.com/fplsblog/confirm_delete.svg',
  help: 'https://storage.googleapis.com/fplsblog/nav-icons-2/clarity_help.svg',
  group: 'https://storage.googleapis.com/fplsblog/teams-icons-01/group.svg',
  info: 'https://storage.googleapis.com/fplsblog/teams-icons-01/info.svg',
  bin: 'https://storage.googleapis.com/fplsblog/teams-icons-01/bin.svg',
  pen: 'https://storage.googleapis.com/fplsblog/teams-icons-01/pen.svg',
};

export const memberRoles = [
  {
    description: 'Can perform all form actions, invite new members and assign roles and permissions',
    value: 'admin',
    text: 'Admin',
  },
  {
    description: 'Can perform all form actions',
    value: 'editor',
    text: 'Editor',
  },
  {
    description: 'Can only view forms and their actions',
    value: 'viewer',
    text: 'Viewer',
  },
  {
    description: 'Can only create forms',
    value: 'creator',
    text: 'Creator',
  },
];

const roleDisplayOrder = {
  owner: 0,
  admin: 1,
  editor: 2,
  viewer: 3,
  creator: 4,
};

// Higher numbers indicate higher priority. This map is used to determine
// the most significant role when a member has multiple roles (including deprecated roles).
const ROLE_PRIORITY_MAP = {
  admin: 5,
  editor: 4,
  viewer: 3,
  creator: 2,
  approver: 1,
};

export const deprecatedRoles = ['approver'];
export const DEPRECATED_ROLE_PLACEHOLDER = 'unassigned';

export const createList = (dict) => Object.values(dict ?? {}) || [];

export const filtered = (listType, members, search) => {
  const cleanedSearch = search.trim().toLowerCase();
  return createList(members)
    .sort((a, b) => roleDisplayOrder[a.roles[0]] - roleDisplayOrder[b.roles[0]])
    .filter(
      (member) =>
        (listType === 'all' || (listType === 'joined' ? member.user : !member.user)) &&
        (member.email?.trim().toLowerCase().includes(cleanedSearch) ||
          member.name?.trim().toLowerCase().includes(cleanedSearch))
    );
};

const resolveDeprecatedRole = (role) => {
  if (deprecatedRoles.includes(role)) return [DEPRECATED_ROLE_PLACEHOLDER];
  else return [role];
};

const getHighestPriorityRole = (roles) => {
  let highestPriorityRole = null;
  let highestPriority = 0;

  roles.forEach((role) => {
    const priority = ROLE_PRIORITY_MAP[role];
    if (priority && priority > highestPriority) {
      highestPriorityRole = role;
      highestPriority = priority;
    }
  });

  return resolveDeprecatedRole(highestPriorityRole);
};

export const resolveLegacyRoles = (roles) => {
  if (roles?.length) {
    if (roles.length === 1) {
      return resolveDeprecatedRole(roles[0]);
    } else {
      return getHighestPriorityRole(roles);
    }
  }
};

export const resolveLegacyRolesForActiveMember = (activeMember = {}) => {
  const resolvedActiveMember = {
    ...activeMember,
    roles: resolveLegacyRoles(activeMember?.roles),
  };

  return resolvedActiveMember;
};

export const resolveLegacyRolesForMemberInJoinedTeams = (joinedTeams = {}) => {
  const resolvedTeams = {};

  for (const teamId in joinedTeams) {
    const { member } = joinedTeams[teamId];
    if (member && member.roles) {
      const resolvedRoles = resolveLegacyRoles([...member.roles]);
      resolvedTeams[teamId] = { ...joinedTeams[teamId], member: { ...member, roles: resolvedRoles } };
    } else {
      resolvedTeams[teamId] = joinedTeams[teamId];
    }
  }

  return resolvedTeams;
};

export const formatDate = (dateString, withFromNow = false) => {
  const date = new Date(dateString);
  const month = date.toLocaleString('default', { month: 'long' });
  const year = date.getFullYear();
  const day = date.getDate();

  // Add suffix to day
  let dayWithSuffix;
  if (day === 1 || day === 21 || day === 31) {
    dayWithSuffix = day + 'st';
  } else if (day === 2 || day === 22) {
    dayWithSuffix = day + 'nd';
  } else if (day === 3 || day === 23) {
    dayWithSuffix = day + 'rd';
  } else {
    dayWithSuffix = day + 'th';
  }

  if (withFromNow) {
    return `${dayWithSuffix} ${month}, ${year} (${moment.utc(dateString).fromNow()})`;
  }

  return `${dayWithSuffix} ${month}, ${year}`;
};

export const getCurrentUser = (joined, currentTeam) => {
  const { id: teamId, members: currentMembers } = currentTeam;
  const matchingJoinedTeam = joined[teamId];

  let hasJoined = false;
  let joinedDate = '';
  let email = '';
  let role = '';

  if (matchingJoinedTeam) {
    const {
      member: { id: memberId },
    } = matchingJoinedTeam;

    if (currentMembers?.[memberId]) {
      const currentUser = currentMembers[memberId];

      joinedDate = formatDate(currentUser.created);
      email = currentUser.email || '';
      role = currentUser.roles[0];
    }
  }

  if (currentMembers) {
    hasJoined = Object.values(currentMembers).some((m) => m.email === win.EMAIL);
  }

  return {
    actor: {
      joinedDate,
      hasJoined,
      email,
      role,
    },
  };
};
